import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {publishAnalyticsEventForMinorConversion} from "@repo/analytics-learning-time";
import {PageloadMarker} from "@repo/analytics-performance";
import {doNotTranslate} from "@repo/i18n";
import {mediaQueries} from "@repo/shared-styles";
import {staticUrl} from "@repo/urls";

type Props = {
    kind: "HeroKhanmigo";
};

const HeroKhanmigo = ({kind}: Props): React.ReactElement => (
    <View style={styles.heroModule} testId="hero-heading">
        {/* Graphic */}
        <View style={styles.imgWrapper}>
            <img
                className={css(styles.img)}
                src={staticUrl(`/images/lohp/hero_khanmigo_v3@2x.png`)}
                alt=""
                data-testid="hero-image"
            />
        </View>
        <div className={css(styles.content)}>
            {/* Heading */}
            <h1
                data-testid="khanmigo-banner-header"
                className={css(styles.heading)}
            >
                {t`Meet Khanmigo: tutor for learners, sidekick for teachers.`}
            </h1>

            {/* Copy */}
            <p className={css(styles.copy)}>
                <span>
                    {t`Khan Academy moves the needle for educators and students. Powered by GPT-4, Khanmigo delivers wow to your teaching and learning experience!`}
                </span>
            </p>

            {/* CTAs */}
            <View style={styles.buttonRow}>
                <Button
                    href="/khanmigo/teacher-tools"
                    data-testid="khanmigo-banner-primary-cta"
                    safeWithNav={() => {
                        return publishAnalyticsEventForMinorConversion({
                            eventType: "AiGuideLOHPBannerTeacherClicked",
                            eventSchemaVersion: 1,
                        });
                    }}
                >
                    {doNotTranslate`Teacher Tools for FREE`}
                </Button>
                <Button
                    href="/khanmigo/checkout"
                    kind="tertiary"
                    data-testid="khanmigo-banner-secondary-cta"
                    safeWithNav={() => {
                        return publishAnalyticsEventForMinorConversion({
                            eventType: "AiGuideLOHPBannerLearnerClicked",
                            eventSchemaVersion: 1,
                        });
                    }}
                >
                    {doNotTranslate`For Learners`}
                </Button>
                <Button
                    href="https://districts.khanacademy.org/khanmigo"
                    kind="tertiary"
                    data-testid="khanmigo-banner-tertiary-cta"
                    safeWithNav={() => {
                        return publishAnalyticsEventForMinorConversion({
                            eventType: "AiGuideLOHPBannerDistrictClicked",
                            eventSchemaVersion: 1,
                        });
                    }}
                >
                    {doNotTranslate`For Districts`}
                </Button>
            </View>
        </div>
        <PageloadMarker pageName="logged_out_homepage" />
    </View>
);

const styles = StyleSheet.create({
    heroModule: {
        padding: 20,
        color: "#21242C",
        WebkitFontSmoothing: "antialiased",
        flexDirection: "column",
        overflow: "hidden",
        maxWidth: "574px",
        margin: "0 auto",

        [mediaQueries.mdOrLarger]: {
            display: "grid",
            gridTemplateColumns: "41% 1fr",
            padding: "40px 20px 20px",
            gridGap: "30px",
            maxWidth: "1152px",
        },

        [mediaQueries.lgOrLarger]: {
            gridTemplateColumns: "50% 1fr",
        },

        [mediaQueries.xlOrLarger]: {
            gridTemplateColumns: "530px 1fr",
        },
    },
    content: {
        width: "100%",
        paddingBottom: "5px",
        marginTop: "24px",

        [mediaQueries.mdOrLarger]: {
            paddingBottom: "0px",
            marginTop: 0,
            alignSelf: "center",
        },
    },
    imgWrapper: {
        width: "100%",
        alignSelf: "center",
        justifyContent: "center",
        alignItems: "center",
    },
    img: {
        width: "66%",
        maxWidth: 434,

        [mediaQueries.mdOrLarger]: {
            width: "100%",
        },
    },
    heading: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: "24px",
        fontWeight: 600,
        lineHeight: "28px",
        marginBottom: "3%",

        [mediaQueries.smOrSmaller]: {
            textAlign: "center",
        },

        [mediaQueries.smOrLarger]: {
            fontSize: "32px",
            lineHeight: "36px",
        },

        [mediaQueries.mdOrLarger]: {
            fontSize: "28px",
            lineHeight: "32px",
        },

        [mediaQueries.lgOrLarger]: {
            fontSize: "32px",
            lineHeight: "36px",
        },
        [mediaQueries.xlOrLarger]: {
            fontSize: "34px",
            lineHeight: "36px",
        },
    },
    copy: {
        margin: "0 0 20px",
        fontWeight: 400,
        fontSize: "13px",
        lineHeight: "21px",
        width: "100%",

        [mediaQueries.smOrSmaller]: {
            textAlign: "center",
        },

        [mediaQueries.smOrLarger]: {
            fontSize: "16px",
            lineHeight: "24px",
        },

        [mediaQueries.mdOrLarger]: {
            maxWidth: "100%",
            fontSize: "13px",
            lineHeight: "21px",
        },

        [mediaQueries.lgOrLarger]: {
            fontSize: "16px",
            lineHeight: "24px",
        },
    },
    buttonRow: {
        flexDirection: "row",
        justifyContent: "flex-start",
        gap: "32px",

        // Adding custom media query to prevent button overflow
        // @ts-expect-error - TS2322 - not assignable to type 'CSSProperties'.
        "@media screen and (max-width: 832px)": {
            gap: "24px",
        },

        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
        },
    },
});

export default HeroKhanmigo;
